import { clsx } from 'clsx';
import { ReactNode, useMemo } from 'react';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  AutoPricingConstraintType,
  AutoPricingResultType,
  BulkListingPriceCalculation,
  Feature,
  ListingNote,
  ListingPriceCalculation,
  Seating,
  TicketType,
} from 'src/WebApiController';

import { ComparableListing } from '../ComparableListings';
import {
  ComparableListingBadge,
  ComparableListingPrice,
  ComparableListingSeating,
  ComparableListingStates,
} from './ComparableListingDisplay.components';
import * as styles from './ComparableListingDisplay.css';

export type ComparableListingDisplayProps = {
  /**
   * Whether this is the display that represents the current listing.
   */
  listingId: number;
  /**
   * Whether this is the selected listing.
   *
   * This will show an active background state.
   */
  isCurrentListing?: boolean;
  /**
   * Whether this is a listing owned by the current account.
   *
   * This will show a "Your Listing" badge.
   */
  isYourListing?: boolean;
  /**
   * Whether your seller listing is broadcast
   */
  isListed?: boolean;
  isOutlier?: boolean;
  isAnchor?: boolean;
  isFanInventory?: boolean;
  isSelectedWithListingGroup?: boolean;
  title?: ReactNode;
  price?: ReactNode;
  unitCost?: ReactNode;
  validPurchaseQuantities?: number[] | null;
  listingNotes?: ListingNote[];
  seating?: Seating;
  listingPriceCalc?: ListingPriceCalculation | null;
  listingPriceCalculations?: BulkListingPriceCalculation[] | null;
  comparableListings?: ComparableListing[] | null;
  isExcludingFanInventory?: boolean;
  autoPricingResultType?: AutoPricingResultType;
  autoPricingResultConstraintType?: AutoPricingConstraintType;
  shouldShowResultTypeAction?: boolean;
  officeId?: number | null;
  /** POS-9416: B2B marketplace listing ticket type, rendered as the standard ticket-type icon. */
  ticketType?: TicketType | null;
};

export function ComparableListingDisplay({
  listingId,
  isCurrentListing = false,
  isYourListing = false,
  isListed = false,
  isOutlier = false,
  isAnchor = false,
  isFanInventory = false,
  isSelectedWithListingGroup = false,
  title,
  price,
  unitCost,
  validPurchaseQuantities,
  listingNotes,
  seating,
  listingPriceCalc,
  listingPriceCalculations,
  comparableListings,
  isExcludingFanInventory,
  autoPricingResultType,
  autoPricingResultConstraintType,
  shouldShowResultTypeAction,
  officeId,
  ticketType,
}: ComparableListingDisplayProps) {
  const hasYourListingInComps = useUserHasFeature(Feature.YourListingsInComps);

  const shouldGrayOut = useMemo(() => {
    if (isOutlier) {
      return true;
    }
    if (hasYourListingInComps) {
      return isCurrentListing && !isYourListing;
    }
  }, [hasYourListingInComps, isCurrentListing, isOutlier, isYourListing]);

  return (
    <div
      className={clsx(styles.compListingContainer, {
        [styles.highlighted]: isCurrentListing,
        [styles.highlightedInternalComp]: !isCurrentListing && isYourListing,
        [styles.highlightedOutlier]:
          !isYourListing && (isOutlier || isCurrentListing),
      })}
    >
      <Stack direction="column" className={styles.left} gap="s">
        <ComparableListingSeating
          listingId={listingId}
          title={title}
          seating={seating}
          isYourListing={isYourListing}
          shouldGrayOut={shouldGrayOut}
          shouldShowResultTypeAction={shouldShowResultTypeAction}
          autoPricingResultType={autoPricingResultType}
          autoPricingResultConstraintType={autoPricingResultConstraintType}
        />
        <ComparableListingStates
          listingId={listingId}
          isYourListing={isYourListing}
          isListed={isListed}
          isPosListing={isCurrentListing}
          validPurchaseQuantities={validPurchaseQuantities}
          listingNotes={listingNotes}
          shouldGrayOut={shouldGrayOut}
          isFanInventory={isFanInventory}
          ticketType={ticketType}
        />
      </Stack>

      <div className={styles.badgeContainer}>
        <ComparableListingBadge
          listingId={listingId}
          price={price}
          unitCost={unitCost}
          seating={seating}
          listingPriceCalc={listingPriceCalc}
          listingPriceCalculations={listingPriceCalculations}
          comparableListings={comparableListings}
          isYourListing={isYourListing}
          isPosListing={isCurrentListing}
          isAnchor={isAnchor}
          isOutlier={isOutlier}
          isExcludingFanInventory={isExcludingFanInventory}
          isSelectedWithListingGroup={isSelectedWithListingGroup}
          officeId={officeId}
        />
      </div>
      <ComparableListingPrice
        price={price}
        unitCost={unitCost}
        isYourListing={isYourListing}
        isPosListing={isCurrentListing}
        shouldGrayOut={shouldGrayOut}
      />
    </div>
  );
}
